import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Container, Input, Modal, ModalBody, ModalHeader } from "reactstrap";
import { ChevronDown, Eye, Settings, X } from "react-feather";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import { API_BASE } from "../../services/AxiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./SupportTicketsPage.scss";

function formatDepartment(text) {
  if (!text) return "—";

  return text
    .replace(/_/g, " ") // replace underscores with space
    .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize first letter
}

const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"];

const STATUS_LABEL = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
};

const STATUS_BADGE_CLASS = {
  OPEN: "st-badge--open",
  IN_PROGRESS: "st-badge--progress",
  RESOLVED: "st-badge--resolved",
  CLOSED: "st-badge--closed",
};

const PRIORITY_OPTIONS = ["LOW", "MEDIUM", "HIGH"];

const PRIORITY_LABEL = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
};

const PRIORITY_BADGE_CLASS = {
  LOW: "st-badge--low",
  MEDIUM: "st-badge--medium",
  HIGH: "st-badge--high",
};

const EMPTY_FILTERS = () => ({
  search: "",
  date_from: "",
  date_to: "",
  status: "",
  priority: "",
  role_id: "",
  department: "",
  issue_id: "",
  problem_id: "",
  handling_by: "",
});

function fmtDate(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return String(dt);
  }
}

function StatusBadge({ value }) {
  const k = String(value || "").toUpperCase();
  const cls = STATUS_BADGE_CLASS[k] || "st-badge--closed";
  const label = STATUS_LABEL[k] || value || "—";
  return (
    <span className={`st-badge ${cls}`} title={k}>
      {label}
    </span>
  );
}

function PriorityBadge({ value }) {
  const k = String(value || "").toUpperCase();
  const cls = PRIORITY_BADGE_CLASS[k] || "st-badge--low";
  const label = PRIORITY_LABEL[k] || value || "—";
  return (
    <span className={`st-badge ${cls}`} title={k}>
      {label}
    </span>
  );
}

const emptyStats = () => ({
  total: 0,
  open: 0,
  in_progress: 0,
  resolved: 0,
  closed: 0,
});

function countActiveFilters(f) {
  if (!f) return 0;
  let n = 0;
  if (String(f.search || "").trim()) n += 1;
  if (f.date_from) n += 1;
  if (f.date_to) n += 1;
  if (f.status) n += 1;
  if (f.priority) n += 1;
  if (f.role_id) n += 1;
  if (String(f.department || "").trim()) n += 1;
  if (f.issue_id) n += 1;
  if (f.problem_id) n += 1;
  if (String(f.handling_by || "").trim()) n += 1;
  return n;
}

function buildTicketsRequest(page, perPage, a, sortConfig) {
  const roleId = parseInt(String(a.role_id || ""), 10);
  const issueId = parseInt(String(a.issue_id || ""), 10);
  const problemId = parseInt(String(a.problem_id || ""), 10);
  return {
    page,
    per_page: perPage,
    search: String(a.search || "").trim() || undefined,
    date_from: a.date_from || undefined,
    date_to: a.date_to || undefined,
    status: a.status || undefined,
    priority: a.priority || undefined,
    role_id: roleId > 0 ? roleId : undefined,
    department: String(a.department || "").trim() || undefined,
    issue_id: issueId > 0 ? issueId : undefined,
    problem_id: problemId > 0 ? problemId : undefined,
    sort_by: sortConfig.sort_by,
    sort_dir: sortConfig.sort_dir,
  };
}

/**
 * Single control: type to filter, pick from list, or choose “all”.
 * Uses mousedown on options so blur does not steal the click.
 */
function ComboboxFilterSelect({ label, value, onChange, options, placeholder, allLabel }) {
  const listId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );

  useEffect(() => {
    if (!open) {
      setText(selected ? selected.label : "");
    }
  }, [open, value, selected, options]);

  const filtered = useMemo(() => {
    const qq = text.trim().toLowerCase();
    if (!qq) return options;
    return options.filter((o) => String(o.label || "").toLowerCase().includes(qq));
  }, [options, text]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (val) => {
    const v = val === "" || val === undefined ? "" : String(val);
    onChange(v);
    const opt = v ? options.find((o) => String(o.value) === v) : null;
    setText(opt ? opt.label : "");
    setOpen(false);
  };

  const allRowLabel = allLabel || `All ${label}`;
  const hasValue = String(value ?? "").trim() !== "";

  return (
    <div className="admin-support-tickets__filter-group admin-support-tickets__combo" ref={rootRef}>
      <label className="admin-support-tickets__filter-label">{label}</label>
      <div className={`admin-support-tickets__combo-shell ${open ? "admin-support-tickets__combo-shell--open" : ""}`}>
        <input
          type="text"
          className="admin-support-tickets__combo-input"
          value={text}
          onChange={(e) => {
            const next = e.target.value;
            setText(next);
            setOpen(true);
            if (next.trim() === "") {
              onChange("");
            }
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listId}
        />
        {hasValue ? (
          <button
            type="button"
            className="admin-support-tickets__combo-clear"
            tabIndex={-1}
            aria-label="Clear selection"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick("")}
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        ) : null}
        <button
          type="button"
          className="admin-support-tickets__combo-chevron"
          tabIndex={-1}
          aria-label="Open list"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen((o) => !o)}
        >
          <ChevronDown size={16} strokeWidth={2.2} />
        </button>
        {open ? (
          <ul id={listId} className="admin-support-tickets__combo-list" role="listbox">
            <li role="option">
              <button
                type="button"
                className="admin-support-tickets__combo-option admin-support-tickets__combo-option--all"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick("")}
              >
                {allRowLabel}
              </button>
            </li>
            {filtered.map((o) => (
              <li key={String(o.value)} role="option">
                <button
                  type="button"
                  className={`admin-support-tickets__combo-option ${
                    String(value) === String(o.value) ? "admin-support-tickets__combo-option--active" : ""
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(o.value)}
                >
                  {o.label}
                </button>
              </li>
            ))}
            {filtered.length === 0 ? (
              <li className="admin-support-tickets__combo-empty">No matches</li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

const SupportTicketsPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(() => EMPTY_FILTERS());
  const [filterDraft, setFilterDraft] = useState(() => EMPTY_FILTERS());
  const [sortConfig, setSortConfig] = useState({
    sort_by: "created_at",
    sort_dir: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [stats, setStats] = useState(emptyStats);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [updatingTicketId, setUpdatingTicketId] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);
  const [adminComment, setAdminComment] = useState("");
  // const [filtersOpen, setFiltersOpen] = useState(false);
  const [openActionMenuTicketId, setOpenActionMenuTicketId] = useState(null);
  const [replySeedingTicketId, setReplySeedingTicketId] = useState(null);

  const [roleOptions, setRoleOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [issueOptions, setIssueOptions] = useState([]);
  const [problemOptions, setProblemOptions] = useState([]);
  const [metaLoading, setMetaLoading] = useState(true);

  const apiBase = API_BASE.replace(/\/+$/, "");
  const previewUrl = previewAttachment?.path ? `${apiBase}/${previewAttachment.path}` : "";
  const isPreviewImage = /^image\//i.test(previewAttachment?.mime_type || "");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMetaLoading(true);
      try {
        const ax = AxiosInstance();
        const [rolesRes, deptRes, issRes, probRes] = await Promise.all([
          ax.get("support/roles"),
          ax.get("support/departments"),
          ax.get("support/issues?all=1"),
          ax.get("support/problems?all=1"),
        ]);
        if (cancelled) return;
        setRoleOptions(Array.isArray(rolesRes.data?.data) ? rolesRes.data.data : []);
        const depts = Array.isArray(deptRes.data?.data) ? deptRes.data.data : [];
        setDepartmentOptions(
          depts.map((d) => ({
            value: d,
            label: d
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase()),
          }))
        );
        const issues = Array.isArray(issRes.data?.data) ? issRes.data.data : [];
        setIssueOptions(
          issues.map((i) => ({
            value: i.issue_id,
            label: i.issue_label || i.issue_key || `#${i.issue_id}`,
          }))
        );
        const probs = Array.isArray(probRes.data?.data) ? probRes.data.data : [];
        setProblemOptions(
          probs.map((p) => ({
            value: p.problem_id,
            label: p.problem_label || p.problem_key || `#${p.problem_id}`,
          }))
        );
      } catch (e) {
        if (!cancelled) {
          toast.error("Could not load filter lists (roles / departments / issues / problems).");
        }
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (openActionMenuTicketId == null) return;
    const onDocMouseDown = (e) => {
      if (!e.target.closest(".admin-support-tickets__actions-wrap")) {
        setOpenActionMenuTicketId(null);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [openActionMenuTicketId]);

  const handleReplyInChat = useCallback(
    async (row) => {
      setReplySeedingTicketId(row.ticket_id);
      try {
        const res = await AxiosInstance().post("/admin/support/ticket-chat-seed", {
          ticket_id: row.ticket_id,
        });
        const d = res.data || {};
        const fromApi = d.chat_id != null ? Number(d.chat_id) : 0;
        const fallback = row.reply_chat_id != null ? Number(row.reply_chat_id) : 0;
        const cid = Number.isFinite(fromApi) && fromApi > 0 ? fromApi : fallback;

        if (d.status && Number.isFinite(cid) && cid > 0) {
          if (d.seeded === false && d.message) {
            toast.info(d.message);
          } else if (d.seeded) {
            toast.success(d.message || "Ticket summary was added to the chat.");
          }
          navigate("/job/admin/chat", { state: { openChatId: cid } });
          return;
        }

        toast.error(d.message || "Could not add this ticket to a chat.");
        if (Number.isFinite(fallback) && fallback > 0) {
          navigate("/job/admin/chat", { state: { openChatId: fallback } });
        } else {
          navigate("/job/admin/chat");
        }
      } catch (e) {
        const msg = e.response?.data?.message || e.message || "Request failed";
        toast.error(msg);
        const fallback = row.reply_chat_id != null ? Number(row.reply_chat_id) : 0;
        if (Number.isFinite(fallback) && fallback > 0) {
          navigate("/job/admin/chat", { state: { openChatId: fallback } });
        } else {
          navigate("/job/admin/chat");
        }
      } finally {
        setReplySeedingTicketId(null);
      }
    },
    [navigate]
  );

  const openCommentModal = useCallback((row) => {
    setPendingStatusUpdate({
      ticketId: row.ticket_id,
      status: row.status || "OPEN",
      mode: "email",
      title: row.ticket_id ? `Ticket #${row.ticket_id}` : "Support ticket",
    });
    setAdminComment("");
    setStatusModalOpen(true);
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AxiosInstance().post(
        "/admin/support/tickets",
        buildTicketsRequest(currentPage, perPage, appliedFilters, sortConfig)
      );

      if (!res.data?.status) {
        throw new Error(res.data?.message || "Failed to load tickets");
      }

      setTickets(Array.isArray(res.data.data) ? res.data.data : []);
      setTotalRows(Number(res.data.total || 0));
      if (res.data.stats && typeof res.data.stats === "object") {
        setStats({
          total: Number(res.data.stats.total ?? 0),
          open: Number(res.data.stats.open ?? 0),
          in_progress: Number(res.data.stats.in_progress ?? 0),
          resolved: Number(res.data.stats.resolved ?? 0),
          closed: Number(res.data.stats.closed ?? 0),
        });
      } else {
        setStats(emptyStats());
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to load tickets");
      setTickets([]);
      setTotalRows(0);
      setStats(emptyStats());
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, appliedFilters, sortConfig]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // const openFilterPanel = () => {
  //   setFilterDraft({ ...appliedFilters });
  //   setFiltersOpen(true);
  // };

  // const closeFilterPanel = () => {
  //   setFilterDraft({ ...appliedFilters });
  //   setFiltersOpen(false);
  // };

  const applyFiltersFromPanel = (e) => {
    e.preventDefault();
    setAppliedFilters({ ...filterDraft });
    setCurrentPage(1);
    // setFiltersOpen(false);
  };

  const clearFilterDraft = () => {
    setFilterDraft(EMPTY_FILTERS());
  };

  const resetAllFilters = () => {
    const empty = EMPTY_FILTERS();
    setAppliedFilters(empty);
    setFilterDraft(empty);
    setCurrentPage(1);
  };

  const patchApplied = (patch) => {
    setAppliedFilters((prev) => {
      const next = { ...prev, ...patch };
      setFilterDraft(next);
      return next;
    });
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.sort_by === column) {
        return {
          sort_by: column,
          sort_dir: prev.sort_dir === "asc" ? "desc" : "asc",
        };
      }
      return {
        sort_by: column,
        sort_dir: "asc",
      };
    });
    setCurrentPage(1);
  };

  const renderSortIndicator = (column) => {
    if (sortConfig.sort_by !== column) return null;
    return sortConfig.sort_dir === "asc" ? " ↑" : " ↓";
  };

  const activeFilterCount = useMemo(() => countActiveFilters(appliedFilters), [appliedFilters]);

  const updateStatus = async (ticketId, status, comment) => {
    let adminEmail = "";
    try {
      const userData = JSON.parse(localStorage.getItem("user_data") || "null");
      adminEmail = String(userData?.email || userData?.admin_email || "").trim();
    } catch (_) {
      adminEmail = "";
    }

    setUpdatingTicketId(ticketId);
    try {
      const res = await AxiosInstance().post("/admin/support/update-status", {
        ticket_id: ticketId,
        status,
        admin_comment: comment,
        admin_email: adminEmail || undefined,
      });

      if (!res.data?.status) {
        throw new Error(res.data?.message || "Update failed");
      }

      toast.success(res.data.message || "Ticket updated");
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Update failed");
    } finally {
      setUpdatingTicketId(null);
    }
  };

  const openStatusModal = (ticketId, status, mode = "status") => {
    setPendingStatusUpdate({ ticketId, status, mode });
    setAdminComment("");
    setStatusModalOpen(true);
  };

  const submitStatusUpdate = async () => {
    const comment = adminComment.trim();
    if (!pendingStatusUpdate) return;
    if (!comment) {
      toast.error(
        pendingStatusUpdate?.mode === "email"
          ? "Email reply is required"
          : "Comment is required to update status"
      );
      return;
    }

    await updateStatus(pendingStatusUpdate.ticketId, pendingStatusUpdate.status, comment);
    setStatusModalOpen(false);
    setPendingStatusUpdate(null);
    setAdminComment("");
  };

  const roleLabel = (id) => {
    const r = roleOptions.find((x) => String(x.role_id) === String(id));
    return r ? r.role_label || r.role_key : id;
  };
  const issueLabel = (id) => {
    const o = issueOptions.find((x) => String(x.value) === String(id));
    return o ? o.label : id;
  };
  const problemLabel = (id) => {
    const o = problemOptions.find((x) => String(x.value) === String(id));
    return o ? o.label : id;
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / perPage) || 1);
  const fromRow = totalRows === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const toRow = Math.min(currentPage * perPage, totalRows);

  const titleText = (row) => {
    const issue = String(row.issue_label || row.issue_key || "").trim();
    if (issue) return issue;
    const d = String(row.description || "").trim();
    if (!d) return "—";
    return d.length > 48 ? `${d.slice(0, 45)}…` : d;
  };

  const descText = (row) => {
    const d = String(row.description || "").trim();
    if (!d) return "";
    return d.length > 72 ? `${d.slice(0, 69)}…` : d;
  };

  const renderActivePills = () => {
    const a = appliedFilters;
    const pills = [];
    if (String(a.search || "").trim()) {
      pills.push(
        <span key="search" className="admin-support-tickets__pill">
          Email / search: {a.search.trim()}
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ search: "" })}>
            ×
          </button>
        </span>
      );
    }
    if (a.date_from) {
      pills.push(
        <span key="df" className="admin-support-tickets__pill">
          From: {a.date_from}
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ date_from: "" })}>
            ×
          </button>
        </span>
      );
    }
    if (a.date_to) {
      pills.push(
        <span key="dt" className="admin-support-tickets__pill">
          To: {a.date_to}
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ date_to: "" })}>
            ×
          </button>
        </span>
      );
    }
    if (a.status) {
      pills.push(
        <span key="st" className="admin-support-tickets__pill">
          Status: {STATUS_LABEL[a.status] || a.status}
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ status: "" })}>
            ×
          </button>
        </span>
      );
    }
    if (a.priority) {
      pills.push(
        <span key="pr" className="admin-support-tickets__pill">
          Priority: {PRIORITY_LABEL[a.priority] || a.priority}
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ priority: "" })}>
            ×
          </button>
        </span>
      );
    }
    if (a.role_id) {
      pills.push(
        <span key="role" className="admin-support-tickets__pill">
          Role: {roleLabel(a.role_id)}
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ role_id: "" })}>
            ×
          </button>
        </span>
      );
    }
    if (String(a.department || "").trim()) {
      pills.push(
        <span key="dep" className="admin-support-tickets__pill">
          Department: {a.department.trim()}
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ department: "" })}>
            ×
          </button>
        </span>
      );
    }
    if (a.issue_id) {
      pills.push(
        <span key="iss" className="admin-support-tickets__pill">
          Category: {issueLabel(a.issue_id)}
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ issue_id: "" })}>
            ×
          </button>
        </span>
      );
    }
    if (a.problem_id) {
      pills.push(
        <span key="prob" className="admin-support-tickets__pill">
          Problem: {problemLabel(a.problem_id)}
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ problem_id: "" })}>
            ×
          </button>
        </span>
      );
    }
    if (String(a.handling_by || "").trim()) {
      pills.push(
        <span key="hb" className="admin-support-tickets__pill">
          Handling: {a.handling_by.trim()} (not applied to API)
          <button type="button" className="admin-support-tickets__pill-remove" aria-label="Remove" onClick={() => patchApplied({ handling_by: "" })}>
            ×
          </button>
        </span>
      );
    }
    return pills;
  };

  const setDraft = (patch) => setFilterDraft((prev) => ({ ...prev, ...patch }));

  return (
    <Container fluid>
      <Breadcrumbs mainTitle="Support Tickets" parent="Admin" title="Support Tickets" />

      <div className="admin-support-tickets">
        {/* <div className="admin-support-tickets__header">
          <div>
            <h1 className="admin-support-tickets__title">Support Tickets</h1>
            <p className="admin-support-tickets__breadcrumb">Admin / Support Tickets</p>
          </div>
          <div className="admin-support-tickets__header-actions">
            <span className="admin-support-tickets__count-badge">{totalRows} in view</span>
            <button type="button" className="admin-support-tickets__btn" onClick={() => (filtersOpen ? closeFilterPanel() : openFilterPanel())}>
              <Filter size={15} />
              Filters
              {activeFilterCount > 0 ? (
                <span className="admin-support-tickets__filter-pill-count">{activeFilterCount}</span>
              ) : null}
            </button>
            <button type="button" className="admin-support-tickets__btn admin-support-tickets__btn--ghost" onClick={resetAllFilters}>
              Reset all
            </button>
          </div>
        </div> */}

        {activeFilterCount > 0 ? (
          <div className="admin-support-tickets__active-filters">{renderActivePills()}</div>
        ) : null}

        <div className="admin-support-tickets__stats">
          <div className="admin-support-tickets__stat-card">
            <div className="admin-support-tickets__stat-icon" style={{ background: "#eff6ff" }}>
              🎫
            </div>
            <div>
              <div className="admin-support-tickets__stat-value">{stats.total}</div>
              <div className="admin-support-tickets__stat-label">Total tickets</div>
            </div>
          </div>
          <div className="admin-support-tickets__stat-card">
            <div className="admin-support-tickets__stat-icon" style={{ background: "#eff6ff" }}>
              🔵
            </div>
            <div>
              <div className="admin-support-tickets__stat-value">{stats.open}</div>
              <div className="admin-support-tickets__stat-label">Open</div>
            </div>
          </div>
          <div className="admin-support-tickets__stat-card">
            <div className="admin-support-tickets__stat-icon" style={{ background: "#fffbeb" }}>
              🟡
            </div>
            <div>
              <div className="admin-support-tickets__stat-value">{stats.in_progress}</div>
              <div className="admin-support-tickets__stat-label">In progress</div>
            </div>
          </div>
          <div className="admin-support-tickets__stat-card">
            <div className="admin-support-tickets__stat-icon" style={{ background: "#ecfdf5" }}>
              🟢
            </div>
            <div>
              <div className="admin-support-tickets__stat-value">{stats.resolved}</div>
              <div className="admin-support-tickets__stat-label">Resolved</div>
            </div>
          </div>
          <div className="admin-support-tickets__stat-card">
            <div className="admin-support-tickets__stat-icon" style={{ background: "#f9fafb" }}>
              ⚫
            </div>
            <div>
              <div className="admin-support-tickets__stat-value">{stats.closed}</div>
              <div className="admin-support-tickets__stat-label">Closed</div>
            </div>
          </div>
        </div>

        {/* Always visible filter panel */}
        <div className="admin-support-tickets__filter-panel">
            <div className="admin-support-tickets__filter-title">
              <Settings size={16} className="admin-support-tickets__filter-title-icon" aria-hidden />
              Filter tickets
            </div>
            <form onSubmit={applyFiltersFromPanel}>
              <div className="admin-support-tickets__filter-grid">
                <div className="admin-support-tickets__filter-group">
                  <label className="admin-support-tickets__filter-label">Search by email</label>
                  <input
                    className="admin-support-tickets__filter-input"
                    type="search"
                    value={filterDraft.search}
                    onChange={(e) => setDraft({ search: e.target.value })}
                    placeholder="user@example.com"
                    autoComplete="off"
                  />
                </div>
                <div className="admin-support-tickets__filter-group">
                  <label className="admin-support-tickets__filter-label">Date from</label>
                  <input
                    className="admin-support-tickets__filter-input"
                    type="date"
                    value={filterDraft.date_from}
                    onChange={(e) => setDraft({ date_from: e.target.value })}
                  />
                </div>
                <div className="admin-support-tickets__filter-group">
                  <label className="admin-support-tickets__filter-label">Date to</label>
                  <input
                    className="admin-support-tickets__filter-input"
                    type="date"
                    value={filterDraft.date_to}
                    onChange={(e) => setDraft({ date_to: e.target.value })}
                  />
                </div>
                <div className="admin-support-tickets__filter-group">
                  <label className="admin-support-tickets__filter-label">Status</label>
                  <select
                    className="admin-support-tickets__filter-input"
                    value={filterDraft.status}
                    onChange={(e) => setDraft({ status: e.target.value })}
                  >
                    <option value="">All status</option>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-support-tickets__filter-group">
                  <label className="admin-support-tickets__filter-label">Priority</label>
                  <select
                    className="admin-support-tickets__filter-input"
                    value={filterDraft.priority}
                    onChange={(e) => setDraft({ priority: e.target.value })}
                  >
                    <option value="">All priority</option>
                    {PRIORITY_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_LABEL[p]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-support-tickets__filter-group">
                  <label className="admin-support-tickets__filter-label">Role</label>
                  <select
                    className="admin-support-tickets__filter-input"
                    value={filterDraft.role_id}
                    onChange={(e) => setDraft({ role_id: e.target.value })}
                    disabled={metaLoading}
                  >
                    <option value="">All roles</option>
                    {roleOptions.map((r) => (
                      <option key={r.role_id} value={r.role_id}>
                        {r.role_label || r.role_key}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-support-tickets__filter-group">
                  <label className="admin-support-tickets__filter-label">Department</label>
                  <select
                    className="admin-support-tickets__filter-input"
                    value={filterDraft.department}
                    onChange={(e) => setDraft({ department: e.target.value })}
                    disabled={metaLoading}
                  >
                    <option value="">All departments</option>
                    {departmentOptions.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
                {!metaLoading ? (
                  <ComboboxFilterSelect
                    label="Problem category"
                    value={filterDraft.issue_id}
                    onChange={(v) => setDraft({ issue_id: v })}
                    options={issueOptions}
                    placeholder="Search or select category…"
                    allLabel="All categories"
                  />
                ) : (
                  <div className="admin-support-tickets__filter-group">
                    <label className="admin-support-tickets__filter-label">Problem category</label>
                    <input className="admin-support-tickets__filter-input" disabled placeholder="Loading…" />
                  </div>
                )}
                {!metaLoading ? (
                  <ComboboxFilterSelect
                    label="Problem"
                    value={filterDraft.problem_id}
                    onChange={(v) => setDraft({ problem_id: v })}
                    options={problemOptions}
                    placeholder="Search or select problem…"
                    allLabel="All problems"
                  />
                ) : (
                  <div className="admin-support-tickets__filter-group">
                    <label className="admin-support-tickets__filter-label">Problem</label>
                    <input className="admin-support-tickets__filter-input" disabled placeholder="Loading…" />
                  </div>
                )}
                <div className="admin-support-tickets__filter-group">
                  <label className="admin-support-tickets__filter-label">Handling by</label>
                  <input
                    className="admin-support-tickets__filter-input"
                    type="search"
                    value={filterDraft.handling_by}
                    onChange={(e) => setDraft({ handling_by: e.target.value })}
                    placeholder="Search staff…"
                    disabled
                    title="Assignee filter requires handling_by on tickets (optional future)"
                  />
                </div>
              </div>
              <div className="admin-support-tickets__filter-actions">
                {/* <button type="button" className="admin-support-tickets__btn" onClick={closeFilterPanel}>
                  Cancel
                </button> */}
                <button type="button" className="admin-support-tickets__btn" onClick={clearFilterDraft}>
                  Clear
                </button>
                <button type="submit" className="admin-support-tickets__btn admin-support-tickets__btn--primary">
                  Apply filters
                </button>
              </div>
            </form>
        </div>

        <div className="admin-support-tickets__table-wrap">
          <div className="admin-support-tickets__table-top">
            <div className="admin-support-tickets__table-count">
              {totalRows === 0 ? (
                <span>No tickets</span>
              ) : (
                <>
                  Showing <strong>{fromRow}</strong>–<strong>{toRow}</strong> of <strong>{totalRows}</strong>
                </>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="small text-muted">Rows</span>
              <select
                className="admin-support-tickets__rows-select"
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-support-tickets__table-scroll">
            {loading ? (
              <div className="admin-support-tickets__loading">Loading tickets…</div>
            ) : tickets.length === 0 ? (
              <div className="admin-support-tickets__empty">No support tickets match the current filters.</div>
            ) : (
              <table className="admin-support-tickets__table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort("ticket_id")} style={{ cursor: "pointer" }}>
                      Ticket{renderSortIndicator("ticket_id")}
                    </th>
                    <th onClick={() => handleSort("issue_label")} style={{ cursor: "pointer" }}>
                      Title{renderSortIndicator("issue_label")}
                    </th>
                    <th onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>
                      Email{renderSortIndicator("email")}
                    </th>
                    <th onClick={() => handleSort("role_label")} style={{ cursor: "pointer" }}>
                      Role{renderSortIndicator("role_label")}
                    </th>
                    <th onClick={() => handleSort("department")} style={{ cursor: "pointer" }}>
                      Department{renderSortIndicator("department")}
                    </th>
                    <th onClick={() => handleSort("issue_label")} style={{ cursor: "pointer" }}>
                      Category{renderSortIndicator("issue_label")}
                    </th>
                    <th onClick={() => handleSort("problem_label")} style={{ cursor: "pointer" }}>
                      Problem{renderSortIndicator("problem_label")}
                    </th>
                    <th onClick={() => handleSort("priority")} style={{ cursor: "pointer" }}>
                      Priority{renderSortIndicator("priority")}
                    </th>
                    <th onClick={() => handleSort("status")} style={{ cursor: "pointer" }}>
                      Status{renderSortIndicator("status")}
                    </th>
                    <th>Attachment</th>
                    <th onClick={() => handleSort("created_at")} style={{ cursor: "pointer" }}>
                      Created{renderSortIndicator("created_at")}
                    </th>
                    {/* <th>Update status</th> */}
                    <th className="admin-support-tickets__actions-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((row, i) => (
                    <tr key={row.ticket_id || i}>
                      <td>
                        <span className="admin-support-tickets__ticket-id">#{row.ticket_id}</span>
                      </td>
                      <td>
                        <span className="admin-support-tickets__ticket-title" title={titleText(row)}>
                          {titleText(row)}
                        </span>
                        {descText(row) ? (
                          <span className="admin-support-tickets__ticket-desc" title={row.description}>
                            {descText(row)}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {row.email ? (
                          <a className="admin-support-tickets__email-link" href={`mailto:${row.email}`}>
                            {row.email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <span className="admin-support-tickets__chip">{row.role_label || row.role_key || "—"}</span>
                      </td>
                      <td>{formatDepartment(row.department)}</td>
                      <td>{row.issue_label || row.issue_key || "—"}</td>
                      <td>{row.problem_label || row.problem_key || "—"}</td>
                      <td>
                        <PriorityBadge value={row.priority} />
                      </td>
                      <td>
                        <StatusBadge value={row.status} />
                      </td>
                      <td>
                        {row.attachment?.path ? (
                          <Button
                            color="light"
                            size="sm"
                            title={row.attachment.original_name || "View attachment"}
                            onClick={() => setPreviewAttachment(row.attachment)}
                          >
                            <Eye size={14} />
                          </Button>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <span className="admin-support-tickets__date">{fmtDate(row.created_at)}</span>
                      </td>
                      {/* <td>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={row.status || "OPEN"}
                          disabled={updatingTicketId === row.ticket_id}
                          onChange={(e) => openStatusModal(row.ticket_id, e.target.value)}
                          style={{ minWidth: "130px" }}
                        >
                          {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {STATUS_LABEL[status]}
                            </option>
                          ))}
                        </Input>
                      </td> */}
                      <td className="admin-support-tickets__actions-cell">
                        <div className="admin-support-tickets__actions-wrap">
                          <button
                            type="button"
                            className="admin-support-tickets__dot-btn"
                            aria-label="Row actions"
                            aria-busy={replySeedingTicketId === row.ticket_id}
                            disabled={replySeedingTicketId === row.ticket_id}
                            aria-expanded={openActionMenuTicketId === row.ticket_id}
                            onClick={() =>
                              setOpenActionMenuTicketId((id) =>
                                id === row.ticket_id ? null : row.ticket_id
                              )
                            }
                          >
                            {replySeedingTicketId === row.ticket_id ? "…" : "⋮"}
                          </button>
                          {openActionMenuTicketId === row.ticket_id ? (
                            <div className="admin-support-tickets__actions-menu" role="menu">
                              <button
                                type="button"
                                className="admin-support-tickets__action-item"
                                role="menuitem"
                                disabled={replySeedingTicketId === row.ticket_id}
                                onClick={() => {
                                  setOpenActionMenuTicketId(null);
                                  if (Number(row.user_id || 0) > 0) {
                                    void handleReplyInChat(row);
                                  } else {
                                    openCommentModal(row);
                                  }
                                }}
                              >
                                {Number(row.user_id || 0) > 0 ? "↩ Reply" : "✉ Reply by Email"}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!loading && totalRows > 0 ? (
            <div className="admin-support-tickets__pagination">
              <span className="admin-support-tickets__pag-info">
                Page {currentPage} of {totalPages}
              </span>
              <div className="admin-support-tickets__pag-btns">
                <button
                  type="button"
                  className="admin-support-tickets__pag-btn"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(1)}
                  aria-label="First page"
                >
                  «
                </button>
                <button
                  type="button"
                  className="admin-support-tickets__pag-btn"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="admin-support-tickets__pag-btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  ›
                </button>
                <button
                  type="button"
                  className="admin-support-tickets__pag-btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  aria-label="Last page"
                >
                  »
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <Modal isOpen={Boolean(previewAttachment)} toggle={() => setPreviewAttachment(null)} size="lg" centered>
        <ModalHeader toggle={() => setPreviewAttachment(null)}>
          {previewAttachment?.original_name || "Attachment Preview"}
        </ModalHeader>
        <ModalBody>
          {previewAttachment ? (
            isPreviewImage ? (
              <img
                src={previewUrl}
                alt={previewAttachment.original_name || "Attachment"}
                style={{ width: "100%", maxHeight: "75vh", objectFit: "contain" }}
              />
            ) : (
              <div className="text-center py-4">
                <p className="mb-3">Preview is not available for this file type.</p>
                <a href={previewUrl} target="_blank" rel="noreferrer" className="btn btn-primary">
                  Open attachment
                </a>
              </div>
            )
          ) : null}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={statusModalOpen}
        toggle={() => {
          if (updatingTicketId) return;
          setStatusModalOpen(false);
          setPendingStatusUpdate(null);
          setAdminComment("");
        }}
        centered
      >
        <ModalHeader
          toggle={() => {
            if (updatingTicketId) return;
            setStatusModalOpen(false);
            setPendingStatusUpdate(null);
            setAdminComment("");
          }}
        >
          {pendingStatusUpdate?.mode === "email" ? "Reply by email" : "Update ticket status"}
        </ModalHeader>
        <ModalBody>
          <div className="mb-2">
            <strong>Select ticket status:</strong>
            <div className="d-flex flex-wrap gap-2 mt-2">
              {STATUS_OPTIONS.map((status) => (
                <Button
                  key={status}
                  type="button"
                  color={pendingStatusUpdate?.status === status ? "primary" : "light"}
                  outline={pendingStatusUpdate?.status !== status}
                  size="sm"
                  onClick={() =>
                    setPendingStatusUpdate((prev) =>
                      prev ? { ...prev, status } : prev
                    )
                  }
                  disabled={Boolean(updatingTicketId)}
                >
                  {STATUS_LABEL[status] || status}
                </Button>
              ))}
            </div>
          </div>
          <label className="form-label">
            {pendingStatusUpdate?.mode === "email" ? "Email reply" : "Comment for user"} <span className="text-danger">*</span>
          </label>
          <Input
            type="textarea"
            rows={4}
            value={adminComment}
            onChange={(e) => setAdminComment(e.target.value)}
            placeholder="Write update details for the user..."
            disabled={Boolean(updatingTicketId)}
          />
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              color="light"
              onClick={() => {
                if (updatingTicketId) return;
                setStatusModalOpen(false);
                setPendingStatusUpdate(null);
                setAdminComment("");
              }}
              disabled={Boolean(updatingTicketId)}
            >
              Cancel
            </Button>
            <Button color="primary" onClick={submitStatusUpdate} disabled={Boolean(updatingTicketId)}>
              {updatingTicketId ? "Sending..." : pendingStatusUpdate?.mode === "email" ? "Send email" : "Update status"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </Container>
  );
};

export default SupportTicketsPage;
